import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuList,
  Select,
  Spacer,
  Text,
} from '@chakra-ui/react'
import { dequal as deepEqual } from 'dequal'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { MyOfferDisplayLevel } from '@/features/tableProfiles/constants/tableProfile.constant'
import useTableProfile from '@/features/tableProfiles/useTableProfile'
import useUserTableProfile from '@/features/userTableProfiles/useUserTableProfile'
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { FaCaretDown, FaSave } from 'react-icons/fa'
import TableProfileMenuItem from './TableProfileMenuItem'
import TableProfileSaveModal from './TableProfileSaveModal'

const TableProfileMenu = ({
  userTableProfile,
  tableState,
  setTableState,
  URLSelectedProfileId,
  tableDisplayLevel,
  setTableDisplayLevel,
  allDisplayLevels = [],
}) => {
  const [profiles, setProfiles] = useState([])
  const [selectedProfile, setSelectedProfile] = useState({})
  const [needSave, setNeedSave] = useState(false)

  const [modalDetail, setModalDetail] = useState({
    show: false,
    profiles: [],
    setProfiles: setProfiles,
  })
  const [saveModalDetail, setSaveModalDetail] = useState({
    show: false,
    profiles: [],
    setProfiles: setProfiles,
  })

  const currentProfileName = selectedProfile?.profileName ?? '(-)'
  const canEditCurrentProfile = !selectedProfile?.isCreatedFromAdmin

  //Hooks
  const dispatch = useDispatch()
  const { useUpdateUserTableProfile, useAddAndCreateTableProfile } = useUserTableProfile()

  const { useUpdateTableProfile } = useTableProfile()

  const addAndCreateTableProfile = useAddAndCreateTableProfile()
  const updateUserTableProfile = useUpdateUserTableProfile() //Make Default

  const updateTableProfile = useUpdateTableProfile()
  const { open: openConfirmationModal } = useConfirmationModalManagement()

  useEffect(() => {
    try {
      dispatch(showLoadingOverlay())
      if (Array.isArray(userTableProfile?.profiles)) {
        setProfiles(userTableProfile?.profiles)
      }

      if (URLSelectedProfileId) {
        const selectedProfile = userTableProfile.profiles.find((profile) => profile._id === URLSelectedProfileId)
        if (selectedProfile) {
          setSelectedProfile(selectedProfile)
          setTableState(selectedProfile?.tableDetails)
          setTableDisplayLevel(selectedProfile?.displayLevel)
          return
        }
      }
      if (userTableProfile?.defaultProfile) {
        setSelectedProfile(userTableProfile?.defaultProfile)
      }

      if (userTableProfile?.defaultProfile?.tableDetails) {
        setTableState(userTableProfile?.defaultProfile?.tableDetails)
        setTableDisplayLevel(userTableProfile?.defaultProfile?.displayLevel)
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }, [userTableProfile, URLSelectedProfileId, dispatch])

  useEffect(() => {
    if (!tableState || !selectedProfile?.tableDetails) {
      return
    }
    const isSame = deepEqual(tableState, selectedProfile?.tableDetails)

    if (tableState && !isSame) {
      setSelectedProfile((prev) => ({
        ...prev,
        tableDetails: tableState,
      }))
      setNeedSave(true)
    }
  }, [tableState, selectedProfile?.tableDetails])

  const handleCreateTableProfile = async (newProfileName = '') => {
    try {
      if (!userTableProfile?._id) {
        throw new Error('Missing User Table Profile ID')
      }
      dispatch(showLoadingOverlay())

      let updatePayload = {
        ...selectedProfile,
        tableDetails: {
          ...selectedProfile.tableDetails,
          pagination: {
            pageIndex: 0,
            pageSize: 10,
          },
        },
        id: userTableProfile?._id,
        isCreatedFromAdmin: false,
      }

      if (newProfileName !== '') {
        updatePayload['profileName'] = newProfileName
      }

      const res = await addAndCreateTableProfile.mutateAsync(updatePayload)
      if (res?.data) {
        setProfiles((prev) => [...prev, res.data])
        setSelectedProfile(res?.data)
        setNeedSave(false)
        //Update
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleSelectProfile = async (profileId) => {
    //Check have un-save filter
    try {
      if (needSave && canEditCurrentProfile) {
        const { isConfirmed } = await openConfirmationModal({
          title: 'You have unsaved change on this profile !',
          message: 'Would you like to save before proceed ? ',
        })
        if (isConfirmed) {
          //Stop current action, and don't open approval modal
          await handleSaveProfile()
        }
        setNeedSave(false)
      }
      dispatch(showLoadingOverlay())

      const newSelectedProfile = profiles.find((profile) => profile?._id === profileId)
      if (!newSelectedProfile) {
        console.error('Unexpected Error, missing newSelectedProfile')
        return
      }

      if (!newSelectedProfile?.tableDetails) {
        console.error('Unexpected Error, missing newSelectedProfile')
        return
      }

      setSelectedProfile(newSelectedProfile)
      setTableState(newSelectedProfile?.tableDetails)
      setTableDisplayLevel(newSelectedProfile.displayLevel)
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleSaveProfile = async () => {
    try {
      if (!selectedProfile?._id) {
        throw new Error('[Unexpected Error] Missing User Table Profile ID')
      }
      dispatch(showLoadingOverlay())
      const res = await updateTableProfile.mutateAsync({ id: selectedProfile._id, data: selectedProfile })

      if (res.data) {
        //Update local
        const updatedProfile = res?.data

        setProfiles((prev) => prev.map((profile) => (profile._id === updatedProfile?._id ? updatedProfile : profile)))
      }
      setNeedSave(false)
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleOpenSaveProfileModal = () => {
    setSaveModalDetail({
      show: true,
      canEditCurrentProfile: canEditCurrentProfile,
      onSave: async (type, newProfileName) => {
        if (type === 'newProfile') {
          await handleCreateTableProfile(newProfileName)
        } else {
          await handleSaveProfile()
        }
      },
    })
  }

  const handleSetDefaultProfile = async (profileId) => {
    try {
      if (profileId === userTableProfile?.defaultProfile?._id) {
        //Already Default
        return
      }

      if (!userTableProfile?._id) {
        throw new Error('[Unexpected Error] Missing User Table Profile ID')
      }
      dispatch(showLoadingOverlay())
      const res = await updateUserTableProfile.mutateAsync({
        id: userTableProfile._id,
        data: { defaultProfile: profileId },
      })
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleSaveProfileModal = async ({ profiles, profilesShownInDashboard, profilesShownInSidebar }) => {
    try {
      dispatch(showLoadingOverlay())
      if (!userTableProfile?._id) {
        throw new Error('[Unexpected Error] Missing User Table Profile ID')
      }

      const res = await updateUserTableProfile.mutateAsync({
        id: userTableProfile._id,
        data: { profiles: profiles.map((profile) => profile._id), profilesShownInDashboard, profilesShownInSidebar },
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleOpenTableProfileManagementModal = () => {
    setModalDetail({
      show: true,
      profiles: profiles,
      saveProfiles: handleSaveProfileModal,
      profilesShownInDashboard: userTableProfile?.profilesShownInDashboard || [],
      profilesShownInSidebar: userTableProfile?.profilesShownInSidebar || [],
    })
  }

  const handleChangeDisplayLevel = (level) => {
    setNeedSave(true)
    setTableDisplayLevel(level)
    setSelectedProfile((prev) => {
      return {
        ...prev,
        displayLevel: level,
      }
    })
  }

  return (
    <>
      <Menu>
        <Flex justifyContent={'center'} alignItems={'center'} gap="2">
          <MenuButton
            as={Button}
            py="6"
            colorScheme="gray"
            borderRadius={'10'}
            variant={'ghost'}
            _hover={{
              color: 'teal.500',
            }}
            _active={{
              color: 'teal.500',
            }}
          >
            <FaCaretDown />
          </MenuButton>
          <Box
            border={canEditCurrentProfile ? '' : '2px solid'}
            borderColor={canEditCurrentProfile ? '' : 'gray.400'}
            borderRadius={10}
            px="2"
            color={canEditCurrentProfile ? '' : 'gray.400'}
          >
            <Text fontWeight={'bold'} fontSize={'lg'}>
              {currentProfileName} {needSave ? '*' : ''}
            </Text>
          </Box>

          {needSave && (
            <>
              <Button
                variant={'ghost'}
                fontSize={'lg'}
                size="md"
                _hover={{
                  color: 'teal.500',
                }}
                onClick={handleOpenSaveProfileModal}
                leftIcon={<FaSave size={18} />}
              >
                {canEditCurrentProfile ? 'Save / Save As' : 'Save As'}
              </Button>
            </>
          )}
        </Flex>

        <MenuList>
          <Flex p="2" px="4">
            <Text as="b">Profile</Text>
          </Flex>

          <Flex direction={'column'} title="Profile" maxH="360px" overflowY="scroll">
            {profiles.map((profile, index) => (
              <TableProfileMenuItem
                key={profile._id}
                name={profile.profileName}
                handleSelectProfile={() => handleSelectProfile(profile._id)}
                handleSetDefaultProfile={(e) => {
                  e.stopPropagation()
                  handleSetDefaultProfile(profile?._id)
                }}
                isSelected={profile?._id === selectedProfile?._id}
                needSave={profile.needSave}
                isDefault={profile?._id === userTableProfile?.defaultProfile?._id}
              />
            ))}
          </Flex>
          <MenuDivider />
          <MenuGroup></MenuGroup>
        </MenuList>
      </Menu>

      <Spacer></Spacer>

      <Select w="300px" value={tableDisplayLevel} onChange={(e) => handleChangeDisplayLevel(e.target.value)}>
        {allDisplayLevels.map((level) => (
          <option key={level.value} value={level.value}>
            {level.label}
          </option>
        ))}
      </Select>
      <TableProfileSaveModal modalDetail={saveModalDetail} setModalDetail={setSaveModalDetail} />
    </>
  )
}

export default TableProfileMenu
